/**
 * Room entry — the ONE place a player arrives anywhere (used by `go`
 * traversal, by the `move_player` effect, and by session start).
 *
 * @remarks
 * Entry emits the room's `description` on the `environment` channel — or,
 * on RE-ENTRY to a room that authored one, its `briefDescription` (R18-3,
 * D82: the re-orientation; `look` always speaks the full text through its
 * own path) — then, exactly once per room per session, keyed by
 * `visitedRoomIds`, its `firstVisitNarratives`, in authored order. The
 * slice-end beat rides this one-shot mechanism (design doc §6.4): entering
 * the substation fires its arrival lines because the room is unvisited, and
 * never again.
 *
 * Sharing this helper across traversal, forced moves, and session start is
 * what keeps the one-shot promise TRUE regardless of how the player got
 * there — and (D238) what makes a follower follow every road in: the
 * company the player keeps is read here, in the doorway
 * (`entities/actor-following.ts`). A follower that stops at the threshold
 * says so first, as the player goes; the ones that come through are moved
 * before the page is read, so the page can say they are there; the ones
 * found waiting speak last, if the pack gave them a line.
 *
 * @packageDocumentation
 */

import type { RoomId, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireRoom } from '../bundle/bundle-lookups.js';
import { bumpResponseUsage } from '../defaults/default-responses.js';
import { followingStepOf, speakRejoins, speakThresholdRefusals, withFollowersArrived } from '../entities/actor-following.js';
import { describeRoomInState } from '../narrative/conditional-text.js';
import { droppedHereResponse } from './dropped-here.js';
import { type StampedNarratives, stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';

/** The result of arriving somewhere: advanced cursor, emitted entries, and the moved state. */
export interface RoomEntry extends StampedNarratives {
	readonly state: RuntimeState;
}

/**
 * Move the player into a room and emit its entry narration.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE arrival
 * @param targetRoomId - Where the player arrives
 * @param cursor - Where entry narration lands in the transcript
 * @returns The moved state, the emitted entries, and the advanced cursor
 */
export const enterRoom = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	targetRoomId: RoomId,
	cursor: TranscriptCursor,
): RoomEntry => {
	const room = requireRoom(bundle, targetRoomId);
	const isFirstVisit = !state.progression.visitedRoomIds.includes(targetRoomId);
	// D238: who comes through with the player, who stops, who is found —
	// read in the doorway, before anything moves.
	const step = followingStepOf(bundle, state, targetRoomId);
	const threshold = speakThresholdRefusals(bundle, state, cursor, step.holding);

	/*
	 * D233: the entry's prose is read STANDING IN THE ROOM. Until round C the
	 * description, the dropped-here line and the first-visit beats were all
	 * resolved against the state before the step — the player still, as far
	 * as any condition could tell, in the room they were leaving. Nothing
	 * noticed, because no shipped room's entry prose read the player's
	 * position: flags, items and ledgers are the same from either side of a
	 * doorway. A body is not. The first fragment keyed on `actor_present`
	 * ("the dog has come in after you") read true from the yard, where the
	 * dog was, as the player walked away from it. So the arrival is made
	 * first and the prose reads the world as the player now stands in it —
	 * the only reading in which "here" means the room being described. The
	 * state RETURNED is unchanged in shape; only what the words are judged
	 * against moved. D238: the company the player keeps arrives in the same
	 * breath, for the same reason — "TALLY is at your knee" is the room's
	 * page, read standing in it with TALLY.
	 */
	const arrived: RuntimeState = {
		...state,
		progression: {
			...state.progression,
			activeRoomId: targetRoomId,
			visitedRoomIds: isFirstVisit ? [...state.progression.visitedRoomIds, targetRoomId] : state.progression.visitedRoomIds,
		},
		world: { ...state.world, actorLocations: withFollowersArrived(state.world.actorLocations, step.arriving, targetRoomId) },
	};

	/*
	 * R18-3 (D82, Calder's contract): RE-ENTRY speaks the room's brief form
	 * when one is authored — a re-orientation, not a shorter description.
	 * First entry always speaks the full text (+ first-visit beats below), and
	 * `look` speaks it through its own untouched path, so the long form is one
	 * word away at all times — the escape hatch that makes the compression
	 * safe. Absence of a brief form is the pre-R18 behavior exactly. The
	 * choice derives from `visitedRoomIds`, already-replayable state, so
	 * (bundle, seed, commands) reproduces the brevity byte-for-byte.
	 */
	const description = stampNarrativeLines(
		bundle,
		arrived,
		threshold.cursor,
		// D65: the entry description resolves conditionally too — walking back
		// into a room that went dark has to SAY so (and D82 extends the same
		// law to the brief form: a summary must not contradict the room); D140
		// appends the room's live fragments to whichever form is read.
		[
			{
				priority: 'standard',
				source: 'environment',
				text: describeRoomInState(room, isFirstVisit ? 'full' : 'brief', bundle, arrived),
			},
		],
		`room_entry:${targetRoomId}`,
	);

	// D226 (E8): what the player set down here is the engine's fact about the
	// room, spoken after the pack's paragraph — on a re-entry as on a `look`,
	// because the paragraph was written before the flask was put down. The
	// slot's pool cursor advances in the state returned, since every arrival
	// — a walk, an effect, a checkpoint's return — comes through here.
	const dropped = droppedHereResponse(bundle, arrived, targetRoomId);
	const droppedHere =
		dropped === undefined
			? { cursor: description.cursor, entries: []}
			: stampNarrativeLines(bundle, arrived, description.cursor, [dropped.line], `dropped_here:${targetRoomId}`);

	const firstVisit = isFirstVisit
		? stampNarrativeLines(bundle, arrived, droppedHere.cursor, room.firstVisitNarratives, `first_visit:${targetRoomId}`)
		: { cursor: droppedHere.cursor, entries: []};

	const rejoins = speakRejoins(bundle, arrived, firstVisit.cursor, step.waiting);

	return {
		cursor: rejoins.cursor,
		entries: [...threshold.entries, ...description.entries, ...droppedHere.entries, ...firstVisit.entries, ...rejoins.entries],
		state: {
			...arrived,
			session: {
				...arrived.session,
				responseUsage: bumpResponseUsage(arrived.session.responseUsage, dropped === undefined ? [] : [dropped.slot]),
			},
		},
	};
};
